import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getFolder,
  resolveFolderPath,
  reorderItems,
} from "../../services/folder.service.js";
import "../../styles/visualization/visualizationsPage.css";
import StudioFolderMeta from "../../components/studio/folder/StudioFolderMeta.jsx";
import StudioFolderControls from "../../components/studio/folder/StudioFolderControls.jsx";
import AddItemModal from "../../components/studio/folder/AddItemModal.jsx";
import StudioFolderEntries from "../../components/studio/folder/StudioFolderEntries.jsx";
import StudioFolderPathBar from "../../components/studio/folder/StudioFolderPathBar.jsx";

export default function FolderPage() {
  const ROOT_FOLDER_ID = import.meta.env.VITE_VISUALIZATIONS_FOLDER_ID;
  const { "*": fullPath } = useParams();
  const navigate = useNavigate();

  const folderIds = useMemo(() => {
    const parts = fullPath?.split("/").filter(Boolean) || [];
    return [ROOT_FOLDER_ID, ...parts];
  }, [fullPath]);

  const currentFolderId = folderIds[folderIds.length - 1];

  const [data, setData] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("viz_view_mode") || "grid",
  );

  /* ================= REORDER STATE ================= */
  const [isReordering, setIsReordering] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [localEntries, setLocalEntries] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    localStorage.setItem("viz_view_mode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!error) return;
    const timeoutId = setTimeout(() => {
      setError("");
    }, 6000);
    return () => clearTimeout(timeoutId);
  }, [error]);

  /* ================= FETCH DATA ================= */
  const fetchFolderData = async () => {
    setLoading(true);
    try {
      const [pathRes, folderRes] = await Promise.all([
        resolveFolderPath({ path: folderIds }),
        getFolder(currentFolderId, { page: 1, limit: 200 }),
      ]);

      setFolderPath(pathRes);
      setData(folderRes);
      setLocalEntries(folderRes.folderEntries.records);
      setHasChanges(false);
      setIsReordering(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!folderIds?.length) return;
    fetchFolderData();
  }, [folderIds]);

  /* ================= SAVE ORDER ================= */
  const onReorderItems = async () => {
    if (!localEntries?.length || !hasChanges) return;

    try {
      const folderEntryIds = localEntries.map((e) => e.folderEntryId);
      await reorderItems(currentFolderId, { folderEntryIds });
      await fetchFolderData();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ================= CANCEL ORDER ================= */
  const onCancelReorder = () => {
    setLocalEntries(data.folderEntries.records);
    setHasChanges(false);
    setIsReordering(false);
  };

  if (loading || !data) return <div>Loading...</div>;

  return (
    <div className="visualizations-page">
      <StudioFolderPathBar folderPath={folderPath} />

      <StudioFolderMeta
        folderMetaData={data.folderMetaData}
        folderId={currentFolderId}
        parentPath={folderPath.slice(0, -1)}
        navigate={navigate}
        globalLoading={loading}
        setGlobalLoading={setLoading}
        error={error}
        setError={setError}
      />

      <StudioFolderControls
        viewMode={viewMode}
        setViewMode={setViewMode}
        onAddItem={() => setShowAddModal(true)}
        onReorderItems={onReorderItems}
        hasChanges={hasChanges}
        globalLoading={loading}
        isReordering={isReordering}
        setIsReordering={setIsReordering}
        onCancelReorder={onCancelReorder}
        setGlobalLoading={setLoading}
        setError={setError}
      />

      <StudioFolderEntries
        entries={localEntries}
        currentFolderPath={folderIds} // ✅ UPDATED: pass full path
        viewMode={viewMode}
        loading={loading}
        globalLoading={loading}
        isReordering={isReordering}
        setLocalEntries={setLocalEntries}
        setHasChanges={setHasChanges}
        fetchData={fetchFolderData}
        setGlobalLoading={setLoading}
        setError={setError}
      />

      {showAddModal && (
        <AddItemModal
          folderId={currentFolderId}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchFolderData}
          setError={setError}
        />
      )}
    </div>
  );
}
